import type { ReactNode } from 'react';
import { ProAdvLogo } from '@/components/brand/proadv-logo';

/* ================================================================
   Grain overlay SVG inline — mesmo usado na landing page.
   ================================================================ */
const GRAIN_SVG =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E";

/* ================================================================
   AuthLayout — wrapper de todas as páginas públicas de autenticação.

   Desktop: split 2 colunas (marca | formulário).
   Mobile:  empilha (marca em cima, formulário embaixo).
   ================================================================ */
export function AuthLayout({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#0A0710',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Grain overlay */}
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 0,
          pointerEvents: 'none',
          opacity: 0.035,
          backgroundImage: `url("${GRAIN_SVG}")`,
          backgroundRepeat: 'repeat',
          backgroundSize: '300px 300px',
        }}
      />

      {/* Mesh glows — mesmo padrão da landing */}
      <div aria-hidden="true" style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }}>
        {/* Glow top-left */}
        <div
          style={{
            position: 'absolute',
            top: '-10%',
            left: '-5%',
            width: '60vw',
            height: '60vw',
            maxWidth: 900,
            maxHeight: 900,
            background:
              'radial-gradient(ellipse at center, rgba(109,40,217,0.28) 0%, rgba(109,40,217,0.08) 45%, transparent 70%)',
            filter: 'blur(1px)',
          }}
        />
        {/* Glow bottom-right */}
        <div
          style={{
            position: 'absolute',
            bottom: '-15%',
            right: '-8%',
            width: '55vw',
            height: '55vw',
            maxWidth: 800,
            maxHeight: 800,
            background:
              'radial-gradient(ellipse at center, rgba(139,56,200,0.22) 0%, rgba(80,20,160,0.06) 50%, transparent 70%)',
            filter: 'blur(1px)',
          }}
        />
        {/* Glow center champagne — sotaque */}
        <div
          style={{
            position: 'absolute',
            top: '35%',
            left: '20%',
            width: '30vw',
            height: '30vw',
            maxWidth: 420,
            maxHeight: 420,
            background:
              'radial-gradient(ellipse at center, rgba(232,185,106,0.07) 0%, transparent 65%)',
            filter: 'blur(2px)',
          }}
        />
      </div>

      {/* Layout principal */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 'clamp(24px, 5vw, 48px) clamp(16px, 4vw, 32px)',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 960,
            display: 'grid',
            gridTemplateColumns: 'minmax(0,1fr)',
            gap: 'clamp(32px, 5vw, 64px)',
          }}
          className="auth-layout-grid"
        >
          {/* Painel de marca — coluna esquerda no desktop */}
          <div className="auth-brand-panel" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            {/* Logo */}
            <div style={{ marginBottom: 32 }}>
              <ProAdvLogo variant="horizontal" tone="dark" size={44} />
            </div>

            <h1
              style={{
                fontFamily: "'Fraunces', Georgia, serif",
                fontWeight: 400,
                fontSize: 'clamp(26px, 3.5vw, 40px)',
                lineHeight: 1.15,
                letterSpacing: '-0.02em',
                color: '#F5F1FA',
                margin: '0 0 16px',
              }}
            >
              O sistema jurídico que conversa{' '}
              <span style={{ color: '#E8B96A' }}>com você e com todos os tribunais do Brasil</span>
            </h1>

            <p
              style={{
                fontFamily: "'Hanken Grotesk', sans-serif",
                fontSize: 'clamp(14px, 1.5vw, 16px)',
                color: 'rgba(169,159,184,0.85)',
                lineHeight: 1.65,
                margin: 0,
                maxWidth: 400,
              }}
            >
              Processos, intimações e prazos sincronizados automaticamente
              com o eProc e outros tribunais. Menos tempo em sistemas públicos,
              mais tempo para o que importa.
            </p>

            {/* Destaques */}
            <ul
              style={{
                listStyle: 'none',
                padding: 0,
                margin: '28px 0 0',
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}
            >
              {[
                'Sincronização automática com tribunais',
                'Geração de peças com variáveis reais do processo',
                'Controle de prazos com alerta antecipado',
              ].map((item) => (
                <li
                  key={item}
                  style={{
                    fontFamily: "'Hanken Grotesk', sans-serif",
                    fontSize: 13.5,
                    color: 'rgba(169,159,184,0.75)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      background: 'rgba(232,185,106,0.12)',
                      border: '1px solid rgba(232,185,106,0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      fontSize: 10,
                      color: '#E8B96A',
                    }}
                  >
                    ✓
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Coluna direita — formulário */}
          <div className="auth-form-panel" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            {children}
          </div>
        </div>
      </div>

      {/* Rodapé discreto */}
      <footer
        style={{
          position: 'relative',
          zIndex: 1,
          textAlign: 'center',
          padding: '12px 16px 20px',
          fontFamily: "'Hanken Grotesk', sans-serif",
          fontSize: 12,
          color: 'rgba(169,159,184,0.4)',
        }}
      >
        © {new Date().getFullYear()} VoxisAdv. Todos os direitos reservados.
      </footer>

      {/* Estilos responsivos em linha — sem dependência de classe Tailwind no layout */}
      <style>{`
        @media (min-width: 768px) {
          .auth-layout-grid {
            grid-template-columns: 1fr 1fr !important;
          }
        }
        @media (max-width: 767px) {
          .auth-brand-panel ul {
            display: none !important;
          }
          .auth-brand-panel h1 {
            font-size: 22px !important;
          }
          .auth-brand-panel p {
            font-size: 13px !important;
          }
        }
      `}</style>
    </div>
  );
}
